/* eslint-disable camelcase */
import { all, takeLatest, call, put } from 'redux-saga/effects';
import { navigate } from '@reach/router';
import { ActionType } from 'deox';
import { AxiosError } from 'axios';

import env from '@/env';
import { AuthUser } from '@/types';
import { Paths } from '@/pages/routers';
import {
  registerUserWithEmailActions,
  registerUserWithInviteCodeActions,
  signInActions,
  forgotPasswordActions,
  logoutUserActions,
  activateAccountActions,
  resetPasswordActions,
} from '@/redux/actions/auth';
import { EntityInstance } from '@/services/service-directory/entity';
import { UsersInstance } from '@/services/membership/users';
import authService, { authHelpers } from '@/services/auth';
import { getUserInfoActions } from '@/redux/actions/membership/users';
import { getAuthUser, getSubdomain, getWebAppInfo, persistPageBeforeLogin } from '@/utils';
import { Modal } from 'antd';

export function* registerWithEmailSaga(action: ActionType<typeof registerUserWithEmailActions.request>) {
  try {
    const { email, firstName, lastName, password } = action.payload;

    yield call(authService.registerWithEmail, email, firstName, lastName, password);
    yield put(registerUserWithEmailActions.success());
    navigate(`${Paths.Auth}${Paths.RegisterSuccess}`);
  } catch (err) {
    yield put(registerUserWithEmailActions.failure(err));
  }
}

export function* registerWithInviteCodeSaga(action: ActionType<typeof registerUserWithInviteCodeActions.request>) {
  try {
    const { inviteCode, password } = action.payload;

    yield call(authService.registerWithInviteCode, inviteCode, password);
    navigate(`${Paths.Auth}${Paths.RegisterSuccess}`);
  } catch (err) {
    yield put(registerUserWithInviteCodeActions.failure(err));
  }
}

export function* signInSaga(action: ActionType<typeof signInActions.request>) {
  try {
    const webApp = getWebAppInfo(env.siteId);
    const { email, password } = action.payload;
    const response = yield call(authService.signInWithEmail, email, password);
    const { id_token, access_token, refresh_token } = response;
    const subdomain = getSubdomain();
    const hasSubdomain = subdomain && subdomain !== 'www';

    authHelpers.storeIdToken(id_token);
    authHelpers.storeRefreshToken(refresh_token);
    authHelpers.storeAccessToken(access_token);

    const { roles } = getAuthUser() as AuthUser;
    const hasAdminPermission = roles.includes(env.roles.superAdmin) || roles.includes(env.roles.directoryAdmin);

    try {
      const { data } = yield call(UsersInstance.getUserInfo);
      if (!data.memberships.length) {
        window.location.href = env.rootUrl;
      } else {
        const organisationsOwner = data.memberships.filter(
          (org: { roleName: string }) => org.roleName === env.roles.organisationOwner
        );
        const organisationShouldAccess = !organisationsOwner.length ? data.memberships[0] : organisationsOwner[0];

        authHelpers.storeOrgToken(organisationShouldAccess.token);

        try {
          // User is part of an organisation
          const { subdomain: entitySubdomain } = yield call(
            EntityInstance.fetchEntityById,
            organisationShouldAccess.organisationId
          );
          navigate(
            `https://${hasAdminPermission ? 'www' : entitySubdomain}.${webApp.domain}${Paths.Dashboard}${
              hasSubdomain ? Paths.MyOrganisation : Paths.MyDirectory
            }`
          );
        } catch (error) {
          // User is not part of an organisation
          const errorCode = (error as AxiosError)?.response?.status;
          if (errorCode === 404) {
            // user does not have organisation setup, redirect to setup page
            hasAdminPermission
              ? navigate(`${Paths.Dashboard}${hasSubdomain ? Paths.MyOrganisation : Paths.MyDirectory}`)
              : navigate(`${Paths.Auth}${Paths.BankDetail}`);
          } else {
            yield put(signInActions.failure(new Error('Failed fetching organisation')));
          }
        }
      }
      yield put(getUserInfoActions.success(data.email, data.memberships, data.userId, data?.firstName, data?.lastName));
    } catch (err) {
      yield put(getUserInfoActions.failure(err));
      window.location.href = env.rootUrl;
    }

    yield put(signInActions.success());
  } catch (err) {
    yield put(signInActions.failure(new Error('Login failed due to invalid password or non-activation')));
  }
}

export function* logoutUserSaga() {
  try {
    const webApp = getWebAppInfo(env.siteId);
    authHelpers.clearTokens();
    yield put(logoutUserActions.success());
    navigate(`https://www.${webApp.domain}`);
  } catch (err) {
    yield put(logoutUserActions.failure(err));
  }
}

export function* forgotPasswordSaga(action: ActionType<typeof forgotPasswordActions.request>) {
  try {
    const { identifier } = action.payload;
    const recoveryChannelsResponse = yield call(authService.getPasswordRecoveryOptions, identifier);
    const emailRecoveryChannel = recoveryChannelsResponse.data.find((channel: any) => channel.channelName === 'EMAIL');

    if (emailRecoveryChannel) {
      const { channelId, recoveryCode } = emailRecoveryChannel;
      yield call(authService.initializePasswordRecovery, channelId, recoveryCode);
      Modal.success({
        content: 'Check your email inbox to recover your password.',
        onOk: () => {
          persistPageBeforeLogin();
        },
      });
      yield put(forgotPasswordActions.success());
    } else {
      yield put(forgotPasswordActions.failure(new Error('Unknown error or account does not exist')));
    }
  } catch (err) {
    yield put(forgotPasswordActions.failure(err));
  }
}

export function* activateAccountSaga(action: ActionType<typeof activateAccountActions.request>) {
  try {
    const { activateToken, code } = action.payload;
    yield call(authService.activateAccount, activateToken, code);
    const timeoutRedirect = setTimeout(() => {
      navigate('/auth/login');
      clearTimeout(timeoutRedirect);
    }, 5000);
    yield put(activateAccountActions.success());
  } catch (err) {
    yield put(activateAccountActions.failure(err));
  }
}

export function* resetPasswordSaga(action: ActionType<typeof resetPasswordActions.request>) {
  try {
    const { code, password } = action.payload;
    yield call(authService.resetPassword, code, password);

    Modal.success({
      content: 'Reset password success',
      onOk: () => {
        navigate('/auth/login');
      },
    });
    yield put(resetPasswordActions.success());
  } catch (err) {
    yield put(resetPasswordActions.failure(err));
  }
}

export default function* root() {
  yield all([takeLatest(registerUserWithEmailActions.request, registerWithEmailSaga)]);
  yield all([takeLatest(registerUserWithInviteCodeActions.request, registerWithInviteCodeSaga)]);
  yield all([takeLatest(signInActions.request, signInSaga)]);
  yield all([takeLatest(logoutUserActions.request, logoutUserSaga)]);
  yield all([takeLatest(forgotPasswordActions.request, forgotPasswordSaga)]);
  yield all([takeLatest(activateAccountActions.request, activateAccountSaga)]);
  yield all([takeLatest(resetPasswordActions.request, resetPasswordSaga)]);
}
